import type { JSX } from "preact";
import { useMemo } from "preact/hooks";
import { Select } from "@gss/components/Select";
import type { Filter, MeasurementStat } from "@gss/types/measurementStat";
import {
    PROFESSIONAL_CATEGORY_FILTER_VALUE,
    SELECTION_CRITERIA_FILTER_VALUE,
    SVF_FILTER_VALUE,
    TYPE_OF_VISIT_FILTER_VALUE,
    VERSION_FILTER_VALUE,
} from "../filters/FilterUtil";
import { useTranslation } from "../util/Translation";

interface Props {
    stat: MeasurementStat;
    values: Record<string, string>;
    onChange: (filterValue: string, optionValue: string) => void;
}

const FILTER_VALUES = new Set([
    SELECTION_CRITERIA_FILTER_VALUE,
    VERSION_FILTER_VALUE,
    PROFESSIONAL_CATEGORY_FILTER_VALUE,
    TYPE_OF_VISIT_FILTER_VALUE,
    SVF_FILTER_VALUE,
]);

export function DownloadFilters({
    stat,
    values,
    onChange,
}: Props): JSX.Element | null {
    const { translation, translateFilter } = useTranslation();

    const filters = useMemo(
        () =>
            stat.filters.filter(
                (f) => FILTER_VALUES.has(f.value) && f.lockedOption == null,
            ),
        [stat.filters],
    );

    const renderFilter = (filter: Filter) => {
        if (filter.options == null) {
            return null;
        }

        return (
            <tr key={filter.value}>
                <td className="pe-2">{translateFilter(filter.name)}</td>
                <td>
                    <Select
                        value={values[filter.value] ?? filter.options[0].value}
                        onChange={(value) => onChange(filter.value, value)}
                    >
                        {filter.options.map((opt) => ({
                            value: opt.value,
                            children: translateFilter(opt.name),
                        }))}
                    </Select>
                </td>
            </tr>
        );
    };

    if (filters.length === 0) {
        return null;
    }

    return (
        <>
            <section aria-label={translation.filters.filters}>
                <div className="fst-italic mb-1">
                    {translation.download.filtersDesc}
                </div>

                <table>
                    <tbody>{filters.map(renderFilter)}</tbody>
                </table>
            </section>

            <hr />
        </>
    );
}
